// Shared content hook — used by the Sales Hub, Dashboard, Reports and the /admin editor.
//
// Reads editable content from the Supabase `content` table and KPI targets from
// the `config` table. Anything missing from the database falls back to the
// built-in defaults in src/data/salesPlaybook.js and src/lib/stages.js, so the
// CRM always renders even if the tables are empty or unreachable.
//
// Live updates: subscribes to Supabase Realtime on both tables, so admin edits
// show up for everyone without a refresh (requires Realtime to be enabled on
// `content` and `config` in Supabase; otherwise changes appear on next load).

import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { DEFAULT_TARGETS } from '../lib/stages';
import {
  COMPANY, PRICING_TIERS, VALUE_ADDS, ICP, TRUSTED_BY,
  OBJECTIONS, EMAIL_TEMPLATES, CALL_SCRIPTS, QUICK_FACTS
} from '../data/salesPlaybook';

export { DEFAULT_TARGETS };

// Database key → built-in default
export const DEFAULT_CONTENT = {
  company:         COMPANY,
  quick_facts:     QUICK_FACTS,
  pricing_tiers:   PRICING_TIERS,
  value_adds:      VALUE_ADDS,
  icp:             ICP,
  trusted_by:      TRUSTED_BY,
  objections:      OBJECTIONS,
  email_templates: EMAIL_TEMPLATES,
  call_scripts:    CALL_SCRIPTS
};

const CONTENT_KEYS = Object.keys(DEFAULT_CONTENT);

// Use the DB value only if it's usable; empty arrays/objects fall back to defaults
function pick(dbValue, fallback) {
  if (dbValue == null) return fallback;
  if (Array.isArray(fallback)) return Array.isArray(dbValue) && dbValue.length ? dbValue : fallback;
  if (typeof fallback === 'object') {
    return typeof dbValue === 'object' && Object.keys(dbValue).length ? { ...fallback, ...dbValue } : fallback;
  }
  return dbValue;
}

// Module-level cache so pages don't flash defaults when navigating between them
let cache = null;

export function useContent() {
  const [content, setContent] = useState(cache?.content ?? DEFAULT_CONTENT);
  const [targets, setTargets] = useState(cache?.targets ?? DEFAULT_TARGETS);
  const [loading, setLoading] = useState(!cache);

  const load = useCallback(async () => {
    try {
      const [{ data: contentRows, error: cErr }, { data: configRow, error: tErr }] = await Promise.all([
        supabase.from('content').select('key, value').in('key', CONTENT_KEYS),
        supabase.from('config').select('value').eq('key', 'targets').maybeSingle()
      ]);
      if (cErr) console.warn('[useContent] content load failed:', cErr.message);
      if (tErr) console.warn('[useContent] targets load failed:', tErr.message);

      const fromDb = {};
      for (const row of contentRows || []) fromDb[row.key] = row.value;

      const merged = {};
      for (const key of CONTENT_KEYS) merged[key] = pick(fromDb[key], DEFAULT_CONTENT[key]);

      const t = { ...DEFAULT_TARGETS, ...(configRow?.value || {}) };

      cache = { content: merged, targets: t };
      setContent(merged);
      setTargets(t);
    } catch (err) {
      console.warn('[useContent] load failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const channel = supabase
      .channel(`content-changes-${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'content' }, load)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'config' }, load)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [load]);

  return { content, targets, loading, reload: load };
}

export async function saveContentSection(key, value) {
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase
    .from('content')
    .upsert({ key, value, updated_at: new Date().toISOString(), updated_by: user?.id });
  if (error) throw error;
}

export async function saveTargets(targets) {
  const { error } = await supabase
    .from('config')
    .upsert({ key: 'targets', value: targets, updated_at: new Date().toISOString() });
  if (error) throw error;
}
